import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../Pagination';
import { fmtDateTime } from '../../utils/region';
import { empLogApi, EmployeeChangePage, EmployeeChangeRow } from '../../utils/api/emplog';

// ── Admin › Audit Log › Employee changes (batch 10 R2, PAY-6) ───────────────────────────────────
// employee_change_log, newest first, across every client: who changed which employee, when, through
// which door (the Clients screen, a portal request applied from the SSO panel, the PND.1 allowances, a
// Telegram onboarding or upload), and each field's value before and after.
// ⛔ READ-ONLY, like the log itself: GET /api/admin/employee-changes, behind requireAdmin. The rows
// carry salaries, which is why the view lives here and nowhere else.
// ⛔ Every row, name and value is the server's, shown as recorded; only the labels are in
// src/i18n/mt/emplog.ts.

const FIELDS = ['name', 'name_en', 'id_number', 'sso_id', 'nationality', 'position', 'monthly_salary', 'sso_status', 'termination_date',
  'wht_override', 'sso_override', 'id_doc_id', 'pnd1_allowances', 'source', 'deleted'];
const ACTIONS = ['create', 'update', 'remove', 'restore'];
const card: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-surface)', padding: 16, marginBottom: 14 };
const muted: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 };
const field: React.CSSProperties = { padding: '6px 9px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' };
const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '9px 10px', borderBottom: '1px solid var(--border-strong)', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-primary)', padding: '6px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };

function ChangeCell({ row }: { row: EmployeeChangeRow }) {
  const { t, i18n } = useTranslation();
  // A field batch 10 R3 added (the employee window's — hire date, code, bank …) is named by the payroll
  // module's own label (src/i18n/mt/payroll.ts prl_f_<field>); anything else unknown shows as stored.
  const label = (f: string) => (FIELDS.includes(f) ? t(`ecl_field_${f}`) : i18n.exists(`prl_f_${f}`) ? t(`prl_f_${f}`) : f);
  const val = (v: any) => (v === null || v === undefined || v === '' ? t('ecl_blank') : String(v));
  const act = ACTIONS.includes(row.action) ? t(`ecl_act_${row.action}`) : row.action;
  if (row.action === 'create') {
    // The create row's new_value is the row as stored (JSON): show the fields that were filled in.
    let snap: Record<string, any> = {};
    try { snap = JSON.parse(String(row.new_value || '{}')) || {}; } catch { /* shown as nothing */ }
    const filled = FIELDS.filter(f => f !== 'deleted' && snap[f] !== null && snap[f] !== undefined && snap[f] !== '' && snap[f] !== '[]' && !(f === 'monthly_salary' && Number(snap[f]) === 0));
    return (
      <span data-change="create">
        <b>{act}</b>
        {filled.length > 0 && <span style={{ color: 'var(--text-secondary)' }}> · {filled.map(f => `${label(f)}: ${String(snap[f])}`).join(' · ')}</span>}
      </span>
    );
  }
  return (
    <span data-change={row.action}>
      <b>{act}</b> · {label(row.field)}: <span data-old>{val(row.old_value)}</span> → <span data-new>{val(row.new_value)}</span>
    </span>
  );
}

export default function EmployeeChangeLog() {
  const { t } = useTranslation();
  const [data, setData] = useState<EmployeeChangePage | null>(null);
  const [clientId, setClientId] = useState('');
  const [page, setPage] = useState(1);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setErr('');
    empLogApi.changes({ clientId, page })
      .then(d => { if (alive) setData(d); })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [clientId, page]);

  return (
    <section data-section="employee-changes" style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <i className="ti ti-users" style={{ fontSize: 18, color: 'var(--text-secondary)' }} aria-hidden />
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>{t('ecl_view_employees')}</div>
      </div>
      <div style={{ ...muted, marginBottom: 10 }}>{t('ecl_lead')}</div>
      {err && <div role="alert" style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 10 }}>{t('ecl_load_failed', { error: err })}</div>}
      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '4px 0 8px' }}>
            <select aria-label={t('ecl_filter_client')} value={clientId} onChange={e => { setPage(1); setClientId(e.target.value); }} style={field}>
              <option value="">{t('ecl_all_clients')}</option>
              {data.clients.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
            </select>
            <span data-ecl-count style={{ ...muted, fontSize: 12 }}>{t('ecl_rows', { count: data.total })}</span>
          </div>
          {data.rows.length === 0 ? (
            <div style={{ ...muted, color: 'var(--text-tertiary)' }}>{t('ecl_empty')}</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table aria-label={t('ecl_view_employees')} style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  {['ecl_col_when', 'ecl_col_client', 'ecl_col_employee', 'ecl_col_change', 'ecl_col_who', 'ecl_col_source'].map(k => <th key={k} style={th}>{t(k)}</th>)}
                </tr></thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.id} data-log-id={r.id}>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(r.at)}</td>
                      <td style={td}>{r.client_name || r.client_id}</td>
                      <td style={td}>{r.employee_name || r.employee_id}</td>
                      <td style={td}><ChangeCell row={r} /></td>
                      <td style={td}>{r.actor_name}</td>
                      <td style={{ ...td, fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination page={data.page} total={data.total} limit={data.limit} onPage={setPage} />
            </div>
          )}
        </>
      )}
    </section>
  );
}
